import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Button, Text } from '@tremor/react'
import { XMarkIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function CreateManualQuestionModal({ isOpen, onClose, onSubmit, initialData, isEditing }) {
    const { t } = useTranslation()
    const [formData, setFormData] = useState({
        question: '',
        status: 'draft'
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (initialData) {
            setFormData({
                question: initialData.question || '',
                status: initialData.status || 'draft'
            })
        } else {
            setFormData({
                question: '',
                status: 'draft'
            })
        }
    }, [initialData, isOpen])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!formData.question.trim()) {
            setError(t('Research.QuestaoPartida.Manual.QuestionRequired'))
            return
        }

        if (formData.question.trim().length < 10) {
            setError(t('Research.QuestaoPartida.Manual.QuestionTooShort'))
            return
        }

        if (formData.question.length > 1000) {
            setError(t('Research.QuestaoPartida.Manual.QuestionTooLong'))
            return
        }

        try {
            setLoading(true)
            setError(null)
            // Sanitize data before sending to backend
            const sanitizedData = {
                ...formData,
                question: sanitizeUserInput(formData.question)
            }
            await onSubmit(sanitizedData)
            onClose()
        } catch (err) {
            setError(err.message || t('Research.QuestaoPartida.Manual.SubmitError'))
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setFormData({
            question: '',
            status: 'draft'
        })
        setError(null)
        onClose()
    }

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="mx-auto max-w-lg w-full bg-white rounded-lg shadow-xl">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div className="flex items-center space-x-3">
                            {isEditing ? (
                                <PencilIcon className="h-6 w-6 text-iavisus-100" />
                            ) : (
                                <PlusIcon className="h-6 w-6 text-iavisus-100" />
                            )}
                            <div>
                                <Dialog.Title className="text-lg font-semibold text-gray-900">
                                    {isEditing ? 
                                        t('Research.QuestaoPartida.Manual.EditTitle') : 
                                        t('Research.QuestaoPartida.Manual.CreateTitle')
                                    }
                                </Dialog.Title>
                                <Text className="text-gray-600 mt-1">
                                    {isEditing ? 
                                        t('Research.QuestaoPartida.Manual.EditDescription') : 
                                        t('Research.QuestaoPartida.Manual.CreateDescription')
                                    }
                                </Text>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('Research.QuestaoPartida.Manual.QuestionLabel')}
                                </label>
                                <textarea
                                    id="question"
                                    value={formData.question}
                                    onChange={(e) => handleInputChange('question', e.target.value)}
                                    placeholder={t('Research.QuestaoPartida.Manual.QuestionPlaceholder')}
                                    className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-iavisus-100 focus:border-transparent"
                                    maxLength={1000}
                                    required
                                />
                                <div className="flex justify-between items-center mt-2">
                                    <Text className="text-xs text-gray-500">
                                        {formData.question.length}/1000 {t('Common.Characters')}
                                    </Text>
                                    {formData.question.length >= 10 && (
                                        <Text className="text-xs text-green-600">
                                            ✓ {t('Research.QuestaoPartida.Manual.QuestionValid')}
                                        </Text>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('Research.QuestaoPartida.Manual.StatusLabel')}
                                </label>
                                <select
                                    id="status"
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-iavisus-100 focus:border-transparent"
                                >
                                    <option value="draft">{t('Research.QuestaoPartida.Status.draft')}</option>
                                    <option value="active">{t('Research.QuestaoPartida.Status.active')}</option>
                                    <option value="archived">{t('Research.QuestaoPartida.Status.archived')}</option>
                                </select>
                            </div>

                            {/* Guidelines */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <Text className="text-blue-800 font-medium text-sm mb-2">
                                    {t('Research.QuestaoPartida.Manual.GuidelinesTitle')}
                                </Text>
                                <ul className="text-blue-700 text-xs space-y-1">
                                    <li>• {t('Research.QuestaoPartida.Manual.Guideline1')}</li>
                                    <li>• {t('Research.QuestaoPartida.Manual.Guideline2')}</li>
                                    <li>• {t('Research.QuestaoPartida.Manual.Guideline3')}</li>
                                    <li>• {t('Research.QuestaoPartida.Manual.Guideline4')}</li>
                                </ul>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <Text className="text-red-600 text-sm">{error}</Text>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                {t('Common.Cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={!formData.question.trim() || formData.question.length < 10 || loading}
                                className="disabled:opacity-50"
                            >
                                {loading ? t('Common.Saving') : isEditing ? t('Common.Update') : t('Common.Create')}
                            </Button>
                        </div>
                    </form>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default CreateManualQuestionModal